import React, { useState } from 'react';
import './DataTraceSearch.css';

function DataTraceSearch({ onSearch, onBack, loading }) {
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [state, setState] = useState('MI');
  const [county, setCounty] = useState('Kent');
  const [countyFips, setCountyFips] = useState('081');
  const [apn, setApn] = useState('');

  // Michigan first, then other states alphabetically
  const states = [
    { code: 'MI', name: 'Michigan' },
    { code: '', name: '──────────', disabled: true },
    { code: 'AZ', name: 'Arizona' },
    { code: 'CA', name: 'California' },
    { code: 'CO', name: 'Colorado' },
    { code: 'FL', name: 'Florida' },
    { code: 'NV', name: 'Nevada' },
    { code: 'OR', name: 'Oregon' },
    { code: 'TX', name: 'Texas' },
    { code: 'WA', name: 'Washington' }
  ];

  // Michigan counties: Kent, Ottawa, and surrounding counties first
  const countyOptions = {
    MI: [
      'Kent', 'Ottawa', // Primary
      'Allegan', 'Barry', 'Ionia', 'Montcalm', 'Muskegon', 'Newaygo', // Surrounding
      '──────────', // Separator
      'Genesee', 'Ingham', 'Kalamazoo', 'Macomb', 'Oakland', 'Washtenaw', 'Wayne' // Other major
    ],
    CA: ['Los Angeles', 'Orange', 'San Diego', 'Riverside', 'San Bernardino', 'Santa Clara', 'Alameda', 'Sacramento'],
    TX: ['Harris', 'Dallas', 'Tarrant', 'Bexar', 'Travis', 'Collin'],
    FL: ['Miami-Dade', 'Broward', 'Palm Beach', 'Hillsborough', 'Orange'],
    AZ: ['Maricopa', 'Pima', 'Pinal', 'Yavapai'],
    NV: ['Clark', 'Washoe', 'Carson City'],
    CO: ['Denver', 'El Paso', 'Arapahoe', 'Jefferson', 'Adams'],
    WA: ['King', 'Pierce', 'Snohomish', 'Spokane', 'Clark'],
    OR: ['Multnomah', 'Washington', 'Clackamas', 'Lane', 'Marion']
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (address.trim() || apn.trim()) {
      const searchParams = {
        address: address.trim(),
        city: city.trim(),
        zip: zip.trim(),
        state,
        county,
        countyFips: countyFips.trim(),
        apn: apn.trim()
      };
      onSearch(searchParams);
    }
  };

  const handleStateChange = (e) => {
    setState(e.target.value);
    setCounty('');
    setCountyFips('');
  };

  const handleCountyChange = (e) => {
    const selectedCounty = e.target.value;
    setCounty(selectedCounty);

    // Auto-fill county FIPS code for Kent and Ottawa
    const fipsMap = {
      'Kent': '081',
      'Ottawa': '139'
    };
    setCountyFips(fipsMap[selectedCounty] || '');
  };

  return (
    <div className="datatrace-search">
      <div className="search-header">
        <div>
          <h2>DataTrace Property Search</h2>
          <p className="search-subtitle">Enter property information to retrieve comprehensive data from DataTrace</p>
        </div>
      </div>

      <div className="search-container">
        <div className="datatrace-logo">
          <div className="logo-icon">🏠</div>
          <div className="logo-text">
            <span className="logo-data">Data</span><span className="logo-trace">Trace</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="search-form">
          <div className="form-group">
            <label htmlFor="address">Street Address <span className="required">*</span></label>
            <input
              type="text"
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="e.g., 123 Main St"
              className="form-input"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="e.g., Grand Rapids"
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="zip">ZIP Code</label>
              <input
                type="text"
                id="zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="e.g., 49503"
                className="form-input"
                maxLength="10"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="state">State <span className="required">*</span></label>
              <select
                id="state"
                value={state}
                onChange={handleStateChange}
                className="form-select"
                required
              >
                {states.map(s => (
                  <option
                    key={s.code || s.name}
                    value={s.code}
                    disabled={s.disabled}
                  >
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="county">County <span className="required">*</span></label>
              <select
                id="county"
                value={county}
                onChange={handleCountyChange}
                className="form-select"
                required
              >
                <option value="">Select County</option>
                {countyOptions[state]?.map(c => (
                  <option
                    key={c}
                    value={c === '──────────' ? '' : c}
                    disabled={c === '──────────'}
                  >
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="countyFips">County FIPS Code</label>
            <input
              type="text"
              id="countyFips"
              value={countyFips}
              onChange={(e) => setCountyFips(e.target.value)}
              placeholder="e.g., 081 for Kent, 139 for Ottawa"
              className="form-input"
              maxLength="3"
            />
            <p className="input-hint">Auto-filled for Kent (081) and Ottawa (139) counties</p>
          </div>

          <div className="form-group apn-group">
            <label htmlFor="apn">APN (Optional)</label>
            <input
              type="text"
              id="apn"
              value={apn}
              onChange={(e) => setApn(e.target.value)}
              placeholder="e.g., 41-14-01-100-001"
              className="form-input apn-input"
            />
            <p className="input-hint">Assessor's Parcel Number - helps improve accuracy</p>
          </div>

          <button
            type="submit"
            className="search-button"
            disabled={loading || !address.trim() || !state || !county}
          >
            {loading ? (
              <>
                <span className="button-spinner"></span>
                Searching DataTrace...
              </>
            ) : (
              <>
                <span className="search-icon">🔍</span>
                Search Property
              </>
            )}
          </button>
        </form>
      </div>

      <div className="info-section">
        <h3>What You'll Get</h3>
        <div className="info-cards">
          <div className="info-card">
            <div className="info-icon">📋</div>
            <h4>Property Details</h4>
            <p>Legal description, lot size, zoning, and property characteristics</p>
          </div>
          <div className="info-card">
            <div className="info-icon">👥</div>
            <h4>Ownership History</h4>
            <p>Current and previous owners, vesting information, and transfer dates</p>
          </div>
          <div className="info-card">
            <div className="info-icon">💰</div>
            <h4>Tax Information</h4>
            <p>Assessed values, tax amounts, and payment status</p>
          </div>
          <div className="info-card">
            <div className="info-icon">📜</div>
            <h4>Public Records</h4>
            <p>Recorded documents, liens, and encumbrances</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataTraceSearch;
